import React, { useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Chip, Fade, Paper, Popper, Typography, useTheme } from "@mui/material";
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import { MainHeader, SuccessIcon } from "@eposnow/ui-core";
import DashboardTopMenu from "./topMenu/DashboardTopMenu";
import { DashboardContext } from "../DashboardContext";
import AuthenticatingSpinner from "../../../components/AuthenticatingSpinner";
import HospitalityBusinessDashboard from "./businessDashboard/HospitalityBusinessDashboard";
import RetailBusinessDashboard from "./businessDashboard/RetailBusinessDashboard";
import ComparedHospitalityBusinessDashboard from "./comparedBusinessDashboard/ComparedHospitalityBusinessDashboard";
import { DashboardFiltersContext } from "./filters/DashboardFiltersContext";
import ComparedRetailBusinessDashboard from "./comparedBusinessDashboard/ComparedRetailBusinessDashboard";
import SalesOverviewDashboard from "./businessDashboard/SalesOverviewDashboard";
import ComparedSalesOverviewDashboard from "./comparedBusinessDashboard/ComparedSalesOverviewDashboard";
import FeedbackBanner from "../../../components/feedback/FeedbackBanner";
import FeedbackDialog from "../../../components/feedback/FeedbackDialog";
import ResultFeedbackDialog from "../../../components/feedback/ResultFeedbackDialog";
import MetricsDashboardPanel from "./topMenu/MetricsDashboardPanel";




const DashboardPanel = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { currentDashboard, isLoadingDashboard } = useContext(DashboardContext);
    const { filters } = useContext(DashboardFiltersContext);
    const [title, setTitle] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const printRef = useRef<HTMLDivElement>(null);
    const [bannerOpen, setBannerOpen] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [succesDialogOpen, setSuccesDialogOpen] = useState(false);
    const [metricSidebarOpen, setMetricSidebarOpen] = useState(false);
    const metric = useMemo(() => [
        { name: t('screens.dashboarding.charts.definitions.Discount'), description: t('screens.dashboarding.charts.definitions.DiscountDefinition') },
        { name: t('screens.dashboarding.charts.definitions.GrossMargin'), description: t('screens.dashboarding.charts.definitions.GrossMarginDefinition') },
        { name: t('screens.dashboarding.charts.definitions.Location'), description: t('screens.dashboarding.charts.definitions.LocationDefinition') },
        { name: t('screens.dashboarding.charts.definitions.NetSalesExcludingTax'), description: t('screens.dashboarding.charts.definitions.NetSalesExcludingTaxDefinition') },
        { name: t('screens.dashboarding.charts.definitions.NetSalesIncludingTax'), description: t('screens.dashboarding.charts.definitions.NetSalesIncludingTaxDefinition') },
        { name: t('screens.dashboarding.charts.definitions.RefundQty'), description: t('screens.dashboarding.charts.definitions.RefundQtyDefinition') },
        { name: t('screens.dashboarding.charts.definitions.RefundValue'), description: t('screens.dashboarding.charts.definitions.RefundValueDefinition') },
        { name: t('screens.dashboarding.charts.definitions.GrossSalesIncludingTax'), description: t('screens.dashboarding.charts.definitions.GrossSalesIncludingTaxDefinition') },
        { name: t('screens.dashboarding.charts.definitions.TaxTotal'), description: t('screens.dashboarding.charts.definitions.TaxTotalDefinition') },
        { name: t('screens.dashboarding.charts.definitions.Tender'), description: t('screens.dashboarding.charts.definitions.TenderDefinition') },
        { name: t('screens.dashboarding.charts.definitions.TotalMargin'), description: t('screens.dashboarding.charts.definitions.TotalMarginDefinition') },
        { name: t('screens.dashboarding.charts.definitions.TransactionQty'), description: t('screens.dashboarding.charts.definitions.TransactionQtyDefinition') },
        { name: t('screens.dashboarding.charts.definitions.TransactionVolume'), description: t('screens.dashboarding.charts.definitions.TransactionVolumeDefinition') },
        { name: t('screens.dashboarding.charts.definitions.GrossSalesExcludingTax'), description: t('screens.dashboarding.charts.definitions.GrossSalesExcludingTaxDefinition') },
        { name: t('screens.dashboarding.charts.definitions.AverageTransactionValue'), description: t('screens.dashboarding.charts.definitions.AverageTransactionValueDefinition') },
        { name: t('screens.dashboarding.charts.definitions.ActiveDevices'), description: t('screens.dashboarding.charts.definitions.ActiveDevicesDefinition') },
        { name: t('screens.dashboarding.charts.definitions.ActiveStaff'), description: t('screens.dashboarding.charts.definitions.ActiveStaffDefinition') },
        { name: t('screens.dashboarding.charts.definitions.CustomerDiscount'), description: t('screens.dashboarding.charts.definitions.CustomerDiscountDefinition') },
        { name: t('screens.dashboarding.charts.definitions.BasketDiscount'), description: t('screens.dashboarding.charts.definitions.BasketDiscountDefinition') },
        { name: t('screens.dashboarding.charts.definitions.NetMargin'), description: t('screens.dashboarding.charts.definitions.NetMarginDefinition') },
        { name: t('screens.dashboarding.charts.definitions.TotalGrossRevenue'), description: t('screens.dashboarding.charts.definitions.TotalGrossRevenueDefinition') },
        { name: t('screens.dashboarding.charts.definitions.TotalNetRevenue'), description: t('screens.dashboarding.charts.definitions.TotalNetRevenueDefinition') }
    ], [t]);

    const getFeatureKey = useCallback(() => currentDashboard?.dashboard.titleDetails.i18.nameKey.split('_name')[0] || ''
        , [currentDashboard])

    useEffect(() => {
        let dashboardTitle = t('screens.dashboarding.title');
        let dashboardDescription = t('screens.dashboarding.description');
        if (currentDashboard?.dashboard?.type === 'Standard') {
            dashboardTitle = currentDashboard?.dashboard?.titleDetails?.i18.nameKey ? t(`screens.dashboarding.titleDetails.${currentDashboard.dashboard.titleDetails.i18.nameKey}`) : t('screens.dashboarding.title');
            dashboardDescription = currentDashboard?.dashboard?.titleDetails?.i18.descKey ? t(`screens.dashboarding.titleDetails.${currentDashboard.dashboard.titleDetails.i18.descKey}`) : t('screens.dashboarding.description');
        } else if (currentDashboard?.dashboard?.type === 'custom') {
            dashboardTitle = currentDashboard?.dashboard?.titleDetails?.name ? currentDashboard?.dashboard?.titleDetails?.name : t('screens.dashboarding.title');
            dashboardDescription = currentDashboard?.dashboard?.titleDetails?.desc ? currentDashboard?.dashboard?.titleDetails?.desc : t('screens.dashboarding.desc');
        }
        setTitle(dashboardTitle);
        setDescription(dashboardDescription);
    }, [currentDashboard])

    useEffect(() => {
        setBannerOpen(true);
    }, [currentDashboard, filters.comparisonOpen])


    return (
        <>
        {bannerOpen &&
            <FeedbackBanner
                  text={t('actions.feedbackDashboard')}                  
                  iconElement= { <FeedbackOutlinedIcon sx={{paddingRight: '5px'}}/> }
                  rightButtonText={t('actions.feedback')}
                  leftButtonText={t('actions.gotIt')}
                  onLeftAction={() => setBannerOpen(false)}
                  onRightAction={ () => setDialogOpen(true)}
            />
        }
        <FeedbackDialog 
            dialogOpen={dialogOpen}
            description={t('actions.feedbackDialogDescriptionDashboard')}
            featureKey={getFeatureKey()}
            inputText={t('actions.feedbackInputDashboard')}
            onClose={ () => {setDialogOpen(false)}}
            onSuccess={() => setSuccesDialogOpen(true)}
        />
        <ResultFeedbackDialog 
            successDialogOpen={succesDialogOpen} 
            onClose={()=> setSuccesDialogOpen(false)}
            icon={<SuccessIcon />}
            title={t('actions.feedbackSent')}
            description={t('actions.successFeedbackDescription')}
            leftButtonText={t('actions.goToClassicFeedback')}
            rightButtonText={t('actions.gotIt')}
            onLeftAction={() => (window.location.replace(`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/Dashboard.aspx`))}
            onRightAction={() => {setSuccesDialogOpen(false)}}
            />
            <Box sx={{ display: 'flex', flexDirection: 'row', marginBottom: '8px', marginLeft: '12px' }} >
                <MainHeader
                    title={title}
                    theme={theme} />
                 <Chip
                        sx={{ color:"primary.dark", background:"secondary.main", marginLeft: '8px' }}
                        variant="filled"
                        label={t('screens.dashboarding.beta')}
                      />
            </Box>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                marginLeft: '12px'
            }}>
                <Typography sx={{ marginBottom: '24px' }} variant="body1">{description}</Typography>
                <Button
                    component="button"
                    onClick={() => setMetricSidebarOpen(true)}
                    sx={{
                        textDecoration: 'underline',
                        fontWeight: 600,
                        minWidth: 0,
                        padding: 0,
                        lineHeight: 'inherit',
                        marginBottom: '25px'
                    }}
                >
                    {t('screens.dashboarding.metricsDefinitions')}
                </Button>
            </Box>
            <Box
                sx={{
                    width: '100%',
                    flexGrow: 1,
                    height: 'auto'
                }}
            >
                {isLoadingDashboard &&
                    <AuthenticatingSpinner />
                }
                {!isLoadingDashboard &&
                    <Box
                        sx={{
                            display: 'flex',
                            flexFlow: 'column',
                            width: '100%'
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                            <DashboardTopMenu
                                printRef={printRef}
                                sxProp={{
                                    width: '99.9%',
                                    paddingY: '8px'
                                }}
                            />
                        </Box>
                        <Box ref={printRef}>
                            {currentDashboard?.dashboard?.uiPageKey === 'sdb_business_overview_hospitality' && !(filters.comparisonOpen && filters.comparePeriod) &&
                                <Box >
                                    <HospitalityBusinessDashboard />
                                </Box>
                            }
                            {currentDashboard?.dashboard?.uiPageKey === 'sdb_business_overview_retail' && !(filters.comparisonOpen && filters.comparePeriod)
                                &&
                                <Box>
                                    <RetailBusinessDashboard />
                                </Box>
                            }
                            {currentDashboard?.dashboard?.uiPageKey === 'sdb_business_overview_hospitality' && (filters.comparisonOpen && filters.comparePeriod) &&
                                <ComparedHospitalityBusinessDashboard />
                            }
                            {currentDashboard?.dashboard?.uiPageKey === 'sdb_business_overview_retail' && (filters.comparisonOpen && filters.comparePeriod) &&
                                <ComparedRetailBusinessDashboard />
                            }
                            {currentDashboard?.dashboard?.uiPageKey === 'sdb_sales_overview' && !(filters.comparisonOpen && filters.comparePeriod) &&
                                <SalesOverviewDashboard />
                            }
                            {currentDashboard?.dashboard?.uiPageKey === 'sdb_sales_overview' && (filters.comparisonOpen && filters.comparePeriod) &&
                                <ComparedSalesOverviewDashboard />
                            }
                        </Box>
                    </Box>
                }
            </Box>
            <Popper open={metricSidebarOpen} placement="bottom-end">
                <Fade mountOnEnter unmountOnExit in={metricSidebarOpen}>
                    <Paper
                        sx={{
                            width: 450,
                            height: 'calc(100dvh - 64px)',
                            position: 'fixed',
                            right: 0,
                            top: '64px',
                            borderRadius: 0,
                            boxShadow: 6,
                            backgroundColor: 'background.paper',
                            backgroundImage: 'none',
                        }}
                    >
                        <MetricsDashboardPanel metrics={metric}
                            onClose={() => setMetricSidebarOpen(false)} />
                    </Paper>
                </Fade>
            </Popper>
        </>
    );
}

export default DashboardPanel;
